import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Download,
  ShoppingCart,
  Sparkles,
  Tag,
  Users,
  Star,
  PiggyBank,
  Bell,
  Shield,
  Zap,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { stats, testimonials, howItWorksSteps } from '../mockData';
import { shopAPI } from '../services/api';

const Home = () => {
  const [featuredShops, setFeaturedShops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedShops();
  }, []);

  const fetchFeaturedShops = async () => {
    try {
      setLoading(true);
      const data = await shopAPI.getAll({ page: 1, limit: 12 });
      setFeaturedShops(data.shops);
    } catch (error) {
      console.error('Error fetching featured shops:', error);
    } finally {
      setLoading(false);
    }
  };

  const iconMap = {
    download: Download,
    shoppingCart: ShoppingCart,
    sparkles: Sparkles,
    tag: Tag,
    users: Users,
    star: Star,
    piggyBank: PiggyBank
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-teal-50 to-white -z-10" />
        
        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" />
        <div className="absolute top-40 left-10 w-72 h-72 bg-teal-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
        <div className="absolute -bottom-8 right-1/3 w-72 h-72 bg-orange-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000" />

        <div className="max-w-7xl mx-auto relative">
          <div className="text-center space-y-8">
            <Badge className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200 text-sm px-4 py-1">
              🎉 Over 450.000 danskere sparer penge med RabatHub
            </Badge>
            
            <h1 className="text-5xl md:text-7xl font-bold leading-tight">
              <span className="bg-gradient-to-r from-blue-600 via-teal-500 to-blue-600 bg-clip-text text-transparent">
                Spar Penge
              </span>
              <br />
              <span className="text-gray-900">Med Ét Klik</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
              RabatHub finder automatisk de bedste rabatkoder når du shopper online.
              Nemt, gratis og utrolig effektivt!
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" className="bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white font-semibold shadow-xl hover:shadow-2xl transition-all duration-200 text-lg px-8 py-6">
                <Download className="mr-2 h-5 w-5" />
                Installér Gratis
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-gray-300 hover:border-blue-500 hover:text-blue-600 transition-all duration-200 text-lg px-8 py-6">
                Se Hvordan Det Virker
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-8 pt-8">
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span className="text-gray-600">100% Gratis</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span className="text-gray-600">Ingen Kreditkort</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span className="text-gray-600">Installér På 30 Sekunder</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = iconMap[stat.icon];
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-200">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-teal-400 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
                    <p className="text-sm text-gray-600">{stat.label}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-orange-100 text-orange-700 border-orange-200 mb-4">
              Sådan fungerer det
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Tre Simple Trin
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Start med at spare penge på under et minut
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {howItWorksSteps.map((step, index) => {
              const Icon = iconMap[step.icon];
              return (
                <Card key={index} className="relative border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-8">
                    <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                      {step.step}
                    </div>
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-6 mt-4">
                      <Icon className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-center">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Shops */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
            <div>
              <Badge className="bg-blue-100 text-blue-700 border-blue-200 mb-4">
                Populære Butikker
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Rabatkoder Til Dine
                <br />
                <span className="bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">
                  Yndlingsbutikker
                </span>
              </h2>
            </div>
            <Link to="/butikker">
              <Button variant="outline" className="border-2 border-blue-500 text-blue-600 hover:bg-blue-50">
                Se Alle Butikker
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {loading ? (
              <div className="col-span-full flex justify-center py-10">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
              </div>
            ) : (
              featuredShops.slice(0, 12).map((shop) => (
                <Card key={shop.id} className="group cursor-pointer border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-4">
                    <div className="aspect-square rounded-xl overflow-hidden mb-3 bg-gray-100">
                      <img
                        src={shop.logo_url}
                        alt={shop.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-1 truncate">
                      {shop.name}
                    </h3>
                    <Badge className="bg-green-100 text-green-700 text-xs">
                      Op til {shop.commission_rate}% rabat
                    </Badge>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-teal-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-teal-100 text-teal-700 border-teal-200 mb-4">
              Funktioner
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Hvorfor RabatHub?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-xl">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Bell className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Smart Notifikationer
                </h3>
                <p className="text-gray-600">
                  Få besked når dine yndlingsbutikker har nye rabatkoder eller særlige tilbud.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  100% Sikker
                </h3>
                <p className="text-gray-600">
                  Vi gemmer aldrig dine betalingsoplysninger. Din sikkerhed er vores prioritet.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Lynhurtig
                </h3>
                <p className="text-gray-600">
                  RabatHub tester alle koder på sekunder og finder den bedste rabat automatisk.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-purple-100 text-purple-700 border-purple-200 mb-4">
              Anmeldelser
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Hvad Siger Brugerne?
            </h2>
            <div className="flex items-center justify-center space-x-2 text-2xl">
              <Star className="w-8 h-8 fill-yellow-400 text-yellow-400" />
              <span className="font-bold">4.9/5</span>
              <span className="text-gray-600">fra over 14.000 anmeldelser</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-200">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4">"{testimonial.text}"</p>
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <Badge variant="outline">{testimonial.platform}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 via-teal-500 to-blue-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utd2lkdGg9IjIiIG9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10" />
        
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-8">
            <TrendingUp className="w-10 h-10" />
          </div>
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Klar Til At Spare Penge?
          </h2>
          <p className="text-xl md:text-2xl mb-8 text-blue-100">
            Slut dig til over 450.000 smarte shoppere der sparer penge hver dag
          </p>
          <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-semibold shadow-2xl hover:shadow-3xl transition-all duration-200 text-lg px-10 py-7">
            <Download className="mr-2 h-6 w-6" />
            Installér RabatHub Gratis
          </Button>
          <p className="mt-6 text-blue-100 text-sm">
            ✨ Ingen kreditkort påkrævet · Installér på 30 sekunder
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;